import { createApi } from "@reduxjs/toolkit/query/react";
import { customBaseQuery } from "../../common/reduxQuery/helpers";
import {
  EntityTypeDto,
  EntityTypeCreateRequest,
  EntityTypeUpdateRequest,
  EntityNormalizationDto,
  EntityNormalizationCreateRequest,
  EntityNormalizationUpdateRequest,
} from "../interfaces/configMap";

export const entityTypeApi = createApi({
  reducerPath: "entityTypeApi",
  baseQuery: customBaseQuery(),
  tagTypes: ["EntityType", "Normalization"],
  endpoints: (build) => ({
    getEntityTypes: build.query<EntityTypeDto[], string | void>({
      query: (search) => ({
        url: `/api/v1/entity-type${
          search ? `?search=${encodeURIComponent(search)}` : ""
        }`,
        method: "GET",
      }),
      providesTags: ["EntityType"],
    }),

    getEntityTypeLookup: build.query<
      EntityTypeDto[],
      { search?: string; limit?: number } | void
    >({
      query: (arg) => {
        const params: string[] = [];
        if (arg && arg.search) {
          params.push(`search=${encodeURIComponent(arg.search)}`);
        }
        if (arg && arg.limit && arg.limit > 0) {
          params.push(`limit=${arg.limit}`);
        }
        const qs = params.length ? `?${params.join("&")}` : "";
        return {
          url: `/api/v1/entity-type/lookup${qs}`,
          method: "GET",
        };
      },
      providesTags: ["EntityType"],
    }),

    getEntityType: build.query<EntityTypeDto, string>({
      query: (id) => ({
        url: `/api/v1/entity-type/${id}`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: "EntityType", id }],
    }),

    createEntityType: build.mutation<EntityTypeDto, EntityTypeCreateRequest>({
      query: (data) => ({
        url: "/api/v1/entity-type",
        body: data,
        method: "POST",
      }),
      invalidatesTags: ["EntityType"],
    }),

    updateEntityType: build.mutation<
      EntityTypeDto,
      { id: string; data: EntityTypeUpdateRequest }
    >({
      query: ({ id, data }) => ({
        url: `/api/v1/entity-type/${id}`,
        body: data,
        method: "PUT",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "EntityType",
        { type: "EntityType", id },
      ],
    }),

    // ========== Normalizations ==========
    getAllNormalizations: build.query<
      EntityNormalizationDto[],
      { limit?: number } | void
    >({
      query: (arg) => ({
        url: `/api/v1/entity-normalization${
          arg && arg.limit ? `?limit=${arg.limit}` : ""
        }`,
        method: "GET",
      }),
      providesTags: ["Normalization"],
    }),

    getNormalizations: build.query<EntityNormalizationDto[], string>({
      query: (entityTypeId) => ({
        url: `/api/v1/entity-type/${entityTypeId}/normalization`,
        method: "GET",
      }),
      providesTags: (_result, _error, entityTypeId) => [
        { type: "Normalization", id: entityTypeId },
      ],
    }),

    createNormalization: build.mutation<
      EntityNormalizationDto,
      { entityTypeId: string; data: EntityNormalizationCreateRequest }
    >({
      query: ({ entityTypeId, data }) => ({
        url: `/api/v1/entity-type/${entityTypeId}/normalization`,
        body: data,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { entityTypeId }) => [
        { type: "Normalization", id: entityTypeId },
      ],
    }),

    updateNormalization: build.mutation<
      EntityNormalizationDto,
      {
        entityTypeId: string;
        normId: string;
        data: EntityNormalizationUpdateRequest;
      }
    >({
      query: ({ entityTypeId, normId, data }) => ({
        url: `/api/v1/entity-type/${entityTypeId}/normalization/${normId}`,
        body: data,
        method: "PUT",
      }),
      invalidatesTags: (_result, _error, { entityTypeId }) => [
        { type: "Normalization", id: entityTypeId },
      ],
    }),

    deleteNormalization: build.mutation<
      void,
      { entityTypeId: string; normId: string }
    >({
      query: ({ entityTypeId, normId }) => ({
        url: `/api/v1/entity-type/${entityTypeId}/normalization/${normId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { entityTypeId }) => [
        { type: "Normalization", id: entityTypeId },
      ],
    }),
  }),
});

export const {
  useGetEntityTypesQuery,
  useLazyGetEntityTypesQuery,
  useGetEntityTypeLookupQuery,
  useLazyGetEntityTypeLookupQuery,
  useGetEntityTypeQuery,
  useLazyGetEntityTypeQuery,
  useCreateEntityTypeMutation,
  useUpdateEntityTypeMutation,
  useGetAllNormalizationsQuery,
  useLazyGetAllNormalizationsQuery,
  useGetNormalizationsQuery,
  useCreateNormalizationMutation,
  useUpdateNormalizationMutation,
  useDeleteNormalizationMutation,
} = entityTypeApi;
